import { Store, toImmutable, Immutable } from 'nuclear-js';
import Actions from '../actions';
import DataUtils from 'data/utils';
import TermUtils from 'utils/terms';
import CommonValues from 'resources/common-values';
import SessionStorageUtils from '../session-storage/utils';
import { logErrorToDatadog } from 'utils/analytics';
import moment from 'moment';

export default Store({
    getInitialState() {
        return toImmutable({
            // students: {
            //     <studentBid>: {
            //         studentInfo: [ <roster data> ],
            //         testEvents: [],
            //         termIdsWithTestEvents: [],
            //         selectedTermNumber: termNumber from term object
            //
            //         NOTE: store growthProjections by subject since we will only be projecting from the most recent
            //                    test event for each subject
            //         growthProjections: {<subject>: [<projections>]},
            //         growthPercentiles: {<subject>: [<percentiles>]},
            //         districtAverageDataLoaded: true, // key only exists once districtAverage data loaded for student
            //         loadtime: xxxxxx, // time that this student was loaded
            //         growthGoals:{<subject>: [<goals>]} //Store goals for each subject
            //         normsForGrowthGoals: {<subject>:
            //                                     { <term>:
            //                                         {
            //                                              // Note: The structure below is for growth goals, it will
            //                                              //       be different for performance goals (no CGI/CGP)
            //                                              rit: { 192: { cgi: N.NN, cgp: PP, statusPerc: T } },
            //                                              cgp: { 1: { cgi: Y.YY, rit: ZZZ, statusPerc: Q } },
            //                                              statusPerc: { 1: { cgi: A.AA, cgp: BBB, rit: CCC } },
            //                                         }
            //                                     }
            //                               },
            //         termData: [<terms with grades and iWeeks>],
            //         linkingStudies: {<subject>: {linking studies with cut scores list}},
            //         highlights: {<subject>: [array of highlight objects] },
            //         skillsStudentResourceDoList: [
            //             {
            //                  "studentBid": "2e593cbb-bd24-415f-9700-6fd41bcadca1",
            //                  "...",
            //                  "learningResourceStateEnumToDateMap": {
            //                      "ASSIGNED": 1479482604478
            //                  }
            //             },
            //             {...},
            //             {
            //                 "studentBid": "2e593cbb-bd24-415f-9700-6fd41bcadca1",
            //                 "...",
            //                 "learningResourceStateEnumToDateMap": {
            //                      "ASSIGNED": 1474923906232
            //                  }
            //             }
            //         ],
            //         skillsStudentDashboard: {
            //              "assignedTests": {
            //                  "14d0ddaa-8e74-4e54-9810-d9d6ede9a7f3":
            //                  {...},
            //                  {...}
            //              },
            //              "scaleMap": {
            //                  "MATHEMATICS": {
            //                      "skillDesignationDataList": [
            //                          {...},
            //                          {...}
            //                      ],
            //                      "passagesDesignationDataList": [],
            //                      "baselineDesignationDataList": [],
            //                      "assignedTestBids": [
            //                          "14d0ddaa-8e74-4e54-9810-d9d6ede9a7f3",
            //                           ...,
            //                           "71d5e619-6a44-4536-9608-b6f5048fabb9"
            //                      ]
            //                  }
            //             }
            //         }
            //         loadComplete: true,
            //     }
            // }
            // for more details, see: https://confluence.americas.nwea.pvt/pages/viewpage.action?pageId=71476604
            students: Immutable.Map(),
        });
    },

    initialize() {
        this.on(Actions.student.LOAD_COMBINED_INFO, loadCombinedInfo);
        this.on(Actions.student.ADD_STUDENT_BID, addStudentBid);
        this.on(Actions.student.SET_STUDENT_LOADED, setStudentLoaded);
        this.on(Actions.student.LOAD_STUDENT_TERM_DATA, loadStudentTermData);
        this.on(Actions.student.CHANGE_SELECTED_TERM_NUMBER, updateTermNumber);
        this.on(Actions.student.LOAD_GROWTH_PROJECTIONS, loadGrowthProjections);
        this.on(Actions.student.LOAD_GROWTH_PERCENTILES, loadGrowthPercentiles);
        this.on(Actions.student.CREATE_EMPTY_GROWTH_PROJECTIONS, createEmptyGrowthProjectionsForStudent);
        this.on(Actions.student.CREATE_EMPTY_GROWTH_PERCENTILES, createEmptyGrowthPercentilesForStudent);
        this.on(Actions.student.LOAD_NORM_DATA_FOR_GOAL_SETTING, loadNormDataForGoalSetting);
        this.on(Actions.student.CREATE_EMPTY_NORM_DATA_FOR_GOAL_SETTING, createEmptyNormDataForGoalSetting);
        this.on(Actions.student.STORE_ENTIRE_STUDENT_DATA_MAP, storeEntireDataMap);
        this.on(Actions.student.ADD_OR_UPDATE_GROWTH_GOAL, addOrUpdateGrowthGoal);
        this.on(Actions.student.DELETE_GROWTH_GOAL, deleteGrowthGoal);
        this.on(Actions.student.STORE_LINKING_STUDIES, storeLinkingStudies);
        this.on(Actions.student.CREATE_EMPTY_LINKING_STUDY_DATA, createEmptyLinkingStudyData);
        this.on(Actions.runtime.CLEAR_STATE, (state) => this.getInitialState());
        this.on(Actions.runtime.ADVANCE_TO_ADJACENT_STUDENT, removeOldAdjacentStudent);
        this.on(Actions.student.CLEAR_STUDENT_DATA, clearStudentData);
        this.on(Actions.student.STORE_HIGHLIGHTS_DATA, storeHighlightsData);
        this.on(Actions.student.CREATE_EMPTY_STUDENT_PLACEHOLDER, createEmptyStudentPlaceholder);
        this.on(Actions.student.COMPLETE_STUDENT_LOAD, completeStudentLoad);
    },
});

function removeOldAdjacentStudent(state, payload) {
    const adjacentStudent = DataUtils.findAdjacentStudent(false);
    const studentCount = state.get('students').size;
    /*
     * Added check for student class size of 3. If 3 (or less) do not delete student.
     */
    return adjacentStudent && adjacentStudent.get('studentBid', null) && studentCount > 3
        ? state.deleteIn(['students', adjacentStudent.get('studentBid')])
        : state;
}

function completeStudentLoad(state, studentBid) {
    // at the end of the load, store the data to session storage
    if (state.hasIn(['students', studentBid])) {
        SessionStorageUtils.saveStudentData(studentBid);
    }

    return state;
}

function createEmptyStudentPlaceholder(state) {
    if (state.hasIn(['students', CommonValues.EMPTY_STUDENT_PLACEHOLDER.key])) {
        return state;
    }
    return state.setIn(
        ['students', CommonValues.EMPTY_STUDENT_PLACEHOLDER.key],
        CommonValues.EMPTY_STUDENT_PLACEHOLDER.value
    );
}

function deserializeStudentData(studentData) {
    return studentData.withMutations((s) => {
        // Growth Goals
        let keyToCheck = 'growthGoals';
        const growthGoals = studentData.get(keyToCheck, Immutable.Map());
        growthGoals.forEach((goals, subjectBid) => {
            s.deleteIn([keyToCheck, subjectBid]);
            s.setIn([keyToCheck, parseInt(subjectBid)], goals);
        });
        // Growth Projections
        keyToCheck = 'growthProjections';
        const growthProjections = studentData.get(keyToCheck, Immutable.Map());
        growthProjections.forEach((projections, subjectBid) => {
            s.deleteIn([keyToCheck, subjectBid]);
            s.setIn([keyToCheck, parseInt(subjectBid)], projections);
        });
        // Growth Percentiles (For comparisons)
        keyToCheck = 'growthPercentiles';
        const growthPercentiles = studentData.get(keyToCheck, Immutable.Map());
        growthPercentiles.forEach((projections, subjectBid) => {
            s.deleteIn([keyToCheck, subjectBid]);
            s.setIn([keyToCheck, parseInt(subjectBid)], projections);
        });
        // Linking Studies
        keyToCheck = 'linkingStudies';
        const linkingStudies = studentData.get(keyToCheck, Immutable.Map());
        linkingStudies.forEach((studies, subjectBid) => {
            s.deleteIn([keyToCheck, subjectBid]);
            s.setIn([keyToCheck, parseInt(subjectBid)], studies);
        });
        // Growth Goal Norms
        keyToCheck = 'normsForGrowthGoals';
        const growthGoalNorms = studentData.get(keyToCheck, Immutable.Map());
        growthGoalNorms.forEach((termData, subjectBid) => {
            // this is a problem for the case when there are no norms for a subject
            //    In that case, the data should look like:
            //    { normsForGrowthGoals: {
            //          <subjectBid>: { <termNumber>: {} }
            //    }
            //
            // but this leaves it blank: { normsForGrowthGoals: {} }
            s.deleteIn([keyToCheck, subjectBid]);
            termData.forEach((normCategory, termNumber) => {
                normCategory.forEach((normData, category) => {
                    normData.forEach((topicData, topicBid) => {
                        s.setIn(
                            [keyToCheck, parseInt(subjectBid), parseInt(termNumber), category, parseInt(topicBid)],
                            topicData
                        );
                    });
                });
            });
        });
        // Highlights
        keyToCheck = 'highlights';
        const highlights = studentData.get(keyToCheck, Immutable.Map());
        highlights.forEach((subjectHighlights, subjectBid) => {
            s.deleteIn([keyToCheck, subjectBid]);
            s.setIn([keyToCheck, parseInt(subjectBid)], subjectHighlights);
        });
    });
}

export function clearStudentData(state) {
    return state.set('students', Immutable.Map());
}

export function deleteGrowthGoal(state, payload) {
    if (payload && payload.goal && payload.studentBid) {
        const { studentBid, goal } = payload;
        if (!state.hasIn(['students', studentBid, 'growthGoals', goal.get('subject')])) {
            throw new Error(`cannot find growth goals for goal: ${goal} and studentBid: ${studentBid}`);
        }

        return state.updateIn(['students', studentBid, 'growthGoals', goal.get('subject')], (goals) => {
            return goals.filter((g) => g.get('growthGoalBid') !== payload.goal.get('growthGoalBid'));
        });
    } else {
        throw new Error(
            `deleteGrowthGoal must be passed a payload that includes a goal and student.  payload was: ${JSON.stringify(
                payload
            )}`
        );
    }
}

export function addOrUpdateGrowthGoal(state, payload) {
    if (!payload || !payload.studentBid || !payload.goal || !payload.goal.subject) {
        return state;
    }

    const goal = toImmutable(payload.goal);
    const goalsForSubject = state.getIn(
        ['students', payload.studentBid, 'growthGoals', payload.goal.subject],
        Immutable.List()
    );

    const replaceIndex = goalsForSubject.findIndex((g) => g.get('growthGoalBid') === goal.get('growthGoalBid'));
    const newGoals = replaceIndex === -1 ? goalsForSubject.push(goal) : goalsForSubject.set(replaceIndex, goal);

    return state.setIn(['students', payload.studentBid, 'growthGoals', payload.goal.subject], newGoals);
}

export function loadCombinedInfo(state, payload) {
    if (!payload || !payload.studentBid || !payload.combinedInfo || !state.hasIn(['students', payload.studentBid])) {
        return state;
    }

    const combinedInfo = payload.combinedInfo;
    if (
        !combinedInfo.hasOwnProperty('studentInfo') ||
        !combinedInfo.hasOwnProperty('testEvents') ||
        !combinedInfo.hasOwnProperty('growthGoals')
    ) {
        throw new Error(
            `expected payload to contain studentInfo, testEvents, and growthGoals.  It has ${Object.keys(
                combinedInfo
            )}.`
        );
    }

    const testEvents = toImmutable(combinedInfo.testEvents);
    const termIdsWithTestEvents = TermUtils.findTermIdsFromTestEvents(testEvents);
    const measurementScaleIDsForStudent = toImmutable(combinedInfo.measurementScaleIDsForStudent);
    const removedTestEvents = toImmutable(combinedInfo.removedTestEvents);

    return state.withMutations((s) => {
        s.setIn(['students', payload.studentBid, 'studentInfo'], toImmutable(combinedInfo.studentInfo));
        s.setIn(['students', payload.studentBid, 'loadtime'], moment.utc().valueOf());
        s.setIn(['students', payload.studentBid, 'termIdsWithTestEvents'], toImmutable(termIdsWithTestEvents));
        s.setIn(['students', payload.studentBid, 'selectedTermNumber'], CommonValues.mostRecentTermNumber);
        s.setIn(['students', payload.studentBid, 'testEvents'], testEvents);
        s.setIn(['students', payload.studentBid, 'measurementScaleIDsForStudent'], measurementScaleIDsForStudent);
        s.setIn(['students', payload.studentBid, 'removedTestEvents'], removedTestEvents);

        const allSubjectsAndMsids = DataUtils.combineSubjectsAndMeasurementScaleIDs(
            combinedInfo.measurementScaleIDsForStudent
        );
        const goals = toImmutable(combinedInfo.growthGoals ? combinedInfo.growthGoals : []);
        allSubjectsAndMsids.forEach((sub) => {
            const goalsForSubject = goals.filter((g) => g.get('subject') === sub.get('bid'));
            s.setIn(['students', payload.studentBid, 'growthGoals', sub.get('bid')], goalsForSubject);
        });
    });
}

export function createEmptyNormDataForGoalSetting(state, payload) {
    // for the case when there is no norm data for goal setting for this student
    //      create the ['normsForGrowthGoals', <subject>, <termNumber> ] key for the student so that we
    //      know that this part of the data load is complete
    if (
        !payload ||
        !payload.studentBid ||
        !payload.termNumber ||
        !payload.subject ||
        !state.hasIn(['students', payload.studentBid])
    ) {
        return state;
    }

    return state.setIn(
        ['students', payload.studentBid, 'normsForGrowthGoals', payload.subject, payload.termNumber],
        Immutable.Map()
    );
}

export function loadNormDataForGoalSetting(state, payload) {
    // norm data will look different for growth goals vs. performance goals
    //
    // growth:
    // {
    //      cgp:          {1: {rit: 200, cgi: 2, statusPerc: 3}, 2: {rit: 201, cgi: 2, statusPerc: 4}, etc...},
    //      rit:          {152: {cgi: -1.2, cgp: 1, statusPerc: 1}, 153: {cgi: -1.1, cgp: 2, statusPerc: 1}, etc....},
    //      statusPerc:   {1: {rit: 191, cgi: -1.3, cgp: 1}, 2: {rit: 191, cgi: -1.2, cgp: 2}, etc....},
    // }
    //
    // performance:
    // {
    //      rit:           {151: {statusPerc: 3}, 152: {statusPerc: 4}, etc...},
    //      statusPerc:    {1: {rit: 148}, 2: {rit: 149}, etc...},
    // }
    if (
        !payload ||
        !payload.studentBid ||
        !payload.termNumber ||
        !payload.subject ||
        !payload.data ||
        !state.hasIn(['students', payload.studentBid])
    ) {
        return state;
    }

    // convert all int keys to ints
    const data = Immutable.Map().withMutations((newData) => {
        toImmutable(payload.data).forEach((normData, normCategory) => {
            normData.forEach((categoryData, categoryKey) => {
                newData.setIn([normCategory, parseInt(categoryKey)], categoryData);
            });
        });
    });

    return state.setIn(
        ['students', payload.studentBid, 'normsForGrowthGoals', payload.subject, payload.termNumber],
        data
    );
}

function createEmptyNormGrowthProjectionsForStudent(state, studentBid, sectionName) {
    // for the case when there is no growth projection data for a student,
    //      create the "growthProjections" key for the student so that we
    //      know that this part of the data load is complete
    if (!state.hasIn(['students', studentBid])) {
        return state;
    }
    return state.setIn(['students', studentBid, sectionName], Immutable.Map());
}

export function createEmptyGrowthProjectionsForStudent(state, studentBid) {
    return createEmptyNormGrowthProjectionsForStudent(state, studentBid, 'growthProjections');
}

export function createEmptyGrowthPercentilesForStudent(state, studentBid) {
    return createEmptyNormGrowthProjectionsForStudent(state, studentBid, 'growthPercentiles');
}

function loadNormGrowthProjections(state, payload, sectionName) {
    if (!payload || !payload.projections || !state.hasIn(['students', payload.studentBid])) {
        return state;
    }
    // projections look like this: [
    // {
    //    "predictedScorePercentile": 99,
    //    "startingTerm": 201504,
    //    "predictedScore": 191.6210154471176,
    //    "startingSem": 3.9,
    //    "endingTerm": 201604,
    //    "predictedGrowth": 13.121015447117603,
    //    "grade": 13,
    //    "startingWeek": 4,
    //    "startingRit": 178.5,
    //    "endingWeek": 4,
    //    "subject": 1
    // }
    // ]

    return state.withMutations((s) => {
        toImmutable(payload.projections)
            .groupBy((p) => p.get('subject'))
            .forEach((projections, subject) => {
                s.setIn(['students', payload.studentBid, sectionName, subject], projections);
            });
    });
}

export function loadGrowthProjections(state, payload) {
    return loadNormGrowthProjections(state, payload, 'growthProjections');
}

export function loadGrowthPercentiles(state, payload) {
    return loadNormGrowthProjections(state, payload, 'growthPercentiles');
}

function loadStudentTermData(state, payload) {
    if (!state.hasIn(['students', payload.studentBid])) {
        return state;
    }
    return state.setIn(['students', payload.studentBid, 'termData'], payload.termData); // termData is already Immutable
}

function addStudentBid(state, studentBid) {
    if (state.hasIn(['students', studentBid])) {
        return state;
    }

    return state.setIn(['students', studentBid], Immutable.Map());
}

function setStudentLoaded(state, studentBid) {
    if (!state.hasIn(['students', studentBid])) {
        return state;
    }

    return state.setIn(['students', studentBid, 'isLoaded'], true);
}

function updateTermNumber(state, payload) {
    if (!state.hasIn(['students', payload.studentBid])) {
        return state;
    }
    return state.setIn(['students', payload.studentBid, 'selectedTermNumber'], payload.termNumber);
}

function storeEntireDataMap(state, payload) {
    const deserializedStudentData = deserializeStudentData(payload.studentData);
    return state.setIn(['students', payload.studentBid], deserializedStudentData);
}

export function storeLinkingStudies(state, studiesPayload) {
    if (!state.hasIn(['students', studiesPayload.studentBid])) {
        return state;
    }

    const linkingStudies = toImmutable(studiesPayload.studies);

    const linkingStudyList =
        linkingStudies instanceof Immutable.Map ? Immutable.List.of(linkingStudies) : linkingStudies;

    //We're only interested in storing linking studies with cut scores
    const linkingStudiesWithCutScores = linkingStudyList.filter(
        (ls) => ls.get('cutScores') && ls.get('cutScores').size > 0
    );
    const linkingStudiesToDisplay = linkingStudiesWithCutScores.size > 0 ? linkingStudiesWithCutScores : null;
    const subject = studiesPayload.subjectBid === 7 ? 12 : studiesPayload.subjectBid;
    return state.withMutations((s) => {
        s.setIn(['students', studiesPayload.studentBid, 'linkingStudies', subject], linkingStudiesToDisplay);
    });
}

export function createEmptyLinkingStudyData(state, payload) {
    if (!state.hasIn(['students', payload.studentBid])) {
        return state;
    }
   

    return state.withMutations((s) => {
        if(payload.subjectBid === 7){
            s.setIn(['students', payload.studentBid, 'linkingStudies', 12], Immutable.Map());
        }
        s.setIn(['students', payload.studentBid, 'linkingStudies', payload.subjectBid], Immutable.Map());
    });
}

export function storeHighlightsData(state, payload) {
    if (!state.hasIn(['students', payload.studentBid])) {
        return state;
    }

    const studentTestEvents = state.getIn(['students', payload.studentBid, 'testEvents']);
    const recentSelectedTermNumber = state.getIn(['students', payload.studentBid, 'selectedTermNumber']);
    const selectedTermNumber = DataUtils.findMostRecentTermNumberByTestEvents(studentTestEvents);
    const highlightsData = new Immutable.Map().withMutations((hd) => {
        for (const nameInPayload in payload.highlights) {
            const matchedSubject = CommonValues.subjects.find(
                (subject) => subject.get('highlightsName') == nameInPayload
            );

            const testEventBidForHighlight = payload.highlights[nameInPayload][0].testResultBid;
            const matchedTestEvent = studentTestEvents.find(
                (testEvent) =>
                    testEvent.get('testEventBid') === testEventBidForHighlight &&
                    selectedTermNumber === testEvent.get('termNumber')
            );

            if (matchedTestEvent) {
                hd.set(parseInt(matchedTestEvent.get('subject'), 10), toImmutable(payload.highlights[nameInPayload]));
            }
        }
    });
    return state.setIn(['students', payload.studentBid, 'highlights'], highlightsData);
}
